import { Map, List, Record } from 'immutable';
import { unless, isNil, pipe } from 'ramda';
import { warn } from '@apiEnabler/api-enabler-common/lib/errors';
import { buildDefaultAccountForId } from './accounts';
import { formatDate } from './date-utils';
import { updateSortCode } from './account-utils';
import { parseActivity } from './activities';
import { getClientNameText } from './tpps';
export var Consent = Record({
  accounts: List(),
  activity: Map(),
  bin: '',
  blcin: '',
  channelId: 'CONSENT CHANNEL ID IS MISSING',
  cin: '',
  clientId: 'CONSENT CLIENT ID IS MISSING',
  clientInstructionId: 'CONSENT CLIENT INSTRUCTION ID IS MISSING',
  clientName: 'CONSENT CLIENT NAME IS MISSING',
  cpbCustomerId: '',
  cpbUserId: '',
  createdAt: null,
  createdBy: '',
  error: null,
  expiresAt: null,
  accessExpiresAt: null,
  expiresSoon: false,
  group: 'CONSENT GROUP IS MISSING',
  grouping: 'CONSENT GROUPING IS MISSING',
  accessGrouping: '',
  history: List(),
  activities: List(),
  id: 'CONSENT ID IS MISSING',
  merchantName: '',
  modifiedAt: null,
  name: 'CONSENT NAME IS MISSING',
  payload: Map(),
  status: 'CONSENT STATUS IS MISSING',
  tppId: 'CONSENT TPP ID IS MISSING',
  tppName: 'CONSENT TPP NAME IS MISSING',
  agentName: '',
  clientDisplayName: ''
});
export var ConsentAudit = Record({
  changeAt: null,
  status: 'CONSENT AUDIT STATUS IS MISSING'
});
var unwrap = function unwrap(wrapper) {
  return wrapper.get('consent', Map()).merge(wrapper.delete('consent'));
};
var createAccountsBinder = function createAccountsBinder(customerAccounts) {
  return function (consent) {
    var accessConsentAccounts = consent.getIn(['payload', 'accountIds'], List());
    var accounts = accessConsentAccounts.map(function (id) {
      return customerAccounts.find(function (account) {
        return account.get('accountId') === id;
      }, null, buildDefaultAccountForId(id));
    });
    return consent.set('accounts', accounts);
  };
};
var parseDates = function parseDates(consent) {
  return consent.update('createdAt', function (createdAt) {
    return createdAt && new Date(createdAt);
  }).update('modifiedAt', function (modifiedAt) {
    return modifiedAt && new Date(modifiedAt);
  }).update('expiresAt', function (expiresAt) {
    return expiresAt && new Date(expiresAt);
  }).update('accessExpiresAt', function (accessExpiresAt) {
    return accessExpiresAt ? new Date(accessExpiresAt) : consent.get('expiresAt') && new Date(consent.get('expiresAt'));
  }).updateIn(['payload', 'executeDate'], function (executeDate) {
    return executeDate && new Date(executeDate);
  });
};
function setAccessGrouping(consent) {
  var accessGrouping = consent.get('accessGrouping');
  var grouping = consent.get('grouping');
  return consent.set('accessGrouping', accessGrouping || grouping);
}
var creditorSortCodePath = ['payload', 'creditor', 'agent', 'identification'];
var debtorSortCodePath = ['payload', 'debtor', 'agent', 'identification'];
var timeStampFor = function timeStampFor(access) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'YYYYMMDD';
  return formatDate(access.get('createdAt'), format);
};
var setName = function setName(consent) {
  return consent.set('name', [timeStampFor(consent), consent.get('clientName').replace(/\s/g, ''), consent.get('createdBy').replace(/\s/g, ''), timeStampFor(consent, 'HHmmss')].filter(Boolean).join('-')).set('clientDisplayName', getClientNameText(consent.get('clientName'), consent.get('agentName'), consent.get('channelId')));
};
var toRecord = function toRecord(consent) {
  return new Consent(consent.toObject());
};
var parseConsentActivity = function parseConsentActivity(consent) {
  return consent.update('activity', unless(isNil, parseActivity));
}; // some consents do not have actitities

export function parseWrappedConsents() {
  var consents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : List();
  var accounts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : List();
  var skipUnsupportedConsents = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var consentMapper = pipe(unwrap, createAccountsBinder(accounts), parseDates, parseConsentActivity, updateSortCode(creditorSortCodePath), updateSortCode(debtorSortCodePath), toRecord, setName, setAccessGrouping);
  var unsupportedConsents = [];
  var mappedConsents = consents.reduce(function (acc, next) {
    try {
      return acc.push(consentMapper(next));
    } catch (e) {
      if (!skipUnsupportedConsents) throw e;
      unsupportedConsents.push(next.toJS());
      return acc;
    }
  }, List());
  if (unsupportedConsents.length > 0) warn('Unsupported consents:', unsupportedConsents);
  return mappedConsents;
}
var isRecordProperty = function isRecordProperty(consentRecord) {
  return function (_, key) {
    return consentRecord.has(key);
  };
};
export function updateConsent(consentRecord) {
  var updatedData = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Map();
  return consentRecord.merge(updatedData.filter(isRecordProperty(consentRecord))).update(parseDates);
}
export function indexConsents(list) {
  return list.groupBy(function (consent) {
    return consent.get('id');
  }).map(function (consents) {
    return consents.first();
  });
}
export function parseConsentAudits(audits) {
  var statusLabelMapping = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return audits.map(toConsentAudit).map(parseConsentAuditDates);
}
function toConsentAudit(consent) {
  return new ConsentAudit(consent.toObject());
}
function parseConsentAuditDates(consent) {
  return consent.update('changeAt', function (changeAt) {
    return new Date(changeAt);
  });
}